import React, { useContext } from "react";
import { View, StyleSheet, TouchableOpacity, Platform } from "react-native";
import { NavigationContainer } from "@react-navigation/native";
import { createStackNavigator } from "@react-navigation/stack";
import { HeaderBackButton } from "@react-navigation/elements";
import AsyncStorage from "@react-native-async-storage/async-storage";
import ListView from "./views/ListView/ListView";
import FilterView from "./views/FilterView/FilterView";
import DetailsView from "./views/DetailsView/DetailsView";
import LegendsView from "./views/LegendsView/LegendsView";
import MapView from "./views/MapView";
import StateDetailsView from "./views/StateDetailsView/StateDetailsView";
import { faFilter } from "@fortawesome/free-solid-svg-icons/faFilter";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons/faCircleInfo";
import { faHome } from "@fortawesome/free-solid-svg-icons/faHome";
import { faShareNodes } from "@fortawesome/free-solid-svg-icons/faShareNodes";
import { COLORS, STRINGS } from "./constants";
import { shareDetails, shareDetailsState } from "./utils/shareTrail";
import { FilterContext } from "./utils/filterContext";
import { logAnalyticsEvent } from "./utils/firebase";

const Stack = createStackNavigator();

const linking = {
  config: {
    screens: {
      MapView: "",
      ListView: "#list",
      FilterView: "#filters",
      DetailsView: "trail-details",
      Legends: "#legends",
      StateDetailsView: "state-details",
    },
  },
};

const PERSISTENCE_KEY = "NAVIGATION_STATE_V1";

/**
 * AppCommon component serves as the main navigation container for the application.
 * It sets up a stack-based navigation using `react-navigation` and provides routes
 * for different views like MapView, ListView, FilterView, and DetailsView.
 *
 * The component also includes custom headers with FontAwesome icons for navigation,
 * allowing users to switch between views easily.
 *
 * @param {object} FontAwesomeIcon - The FontAwesomeIcon component passed as a prop to render icons in the headers.
 * @returns {JSX.Element} A navigation container with stack navigation and customized headers.
 */
const AppCommon = ({ FontAwesomeIcon }) => {
  const iconSize = Platform.select({
    ios: 24,
    android: 24,
    web: "2x",
  });

  const [isReady, setIsReady] = React.useState(Platform.OS !== "web"); // Changed to make sure restoreState is called
  const [initialState, setInitialState] = React.useState();

  const { filterState, setFilterState } = useContext(FilterContext);

  React.useEffect(() => {
    logAnalyticsEvent("screen_view", { name: "Trails-AppCommon" });
    const restoreState = async () => {
      console.log("AppCommon.jsx: restoreState() called");
      try {
        // const initialUrl = await Linking.getInitialURL();

        // if (initialUrl == null) {
        // Only restore state if there's no deep link
        const savedStateString = await AsyncStorage.getItem(PERSISTENCE_KEY);
        const state = savedStateString ? JSON.parse(savedStateString) : undefined;

        if (state !== undefined) {
          setInitialState(state);
        }
        // }
      } finally {
        setIsReady(true);
      }
    };

    if (!isReady) {
      restoreState();
    }
  }, [isReady]);

  if (!isReady) {
    return null;
  }

  return (
    <NavigationContainer
      linking={linking}
      initialState={initialState}
      onStateChange={(state) => AsyncStorage.setItem(PERSISTENCE_KEY, JSON.stringify(state))}
    >
      <Stack.Navigator initialRouteName="MapView">
        <Stack.Screen
          name="MapView"
          component={MapView}
          options={({ navigation }) => ({
            title: STRINGS.appTitle,
            headerTitle: STRINGS.appTitle,
            headerTitleStyle: styles.headerTitle,
            headerRight: () => (
              <View style={styles.headerButtonsContainer}>
                <TouchableOpacity
                  style={styles.iconButton}
                  onPress={() =>
                    navigation.navigate("LegendsView", {
                      screen: STRINGS.mapViewTitle,
                    })
                  }
                  aria-label={STRINGS.information}
                >
                  <FontAwesomeIcon icon={faCircleInfo} size={iconSize} color={COLORS.primary} />
                </TouchableOpacity>
                {filterState === "true" && (
                  <TouchableOpacity
                    style={styles.iconButton}
                    onPress={() => navigation.navigate("FilterView")}
                    aria-label={STRINGS.filter}
                  >
                    <FontAwesomeIcon icon={faFilter} size={iconSize} color={COLORS.primary} />
                  </TouchableOpacity>
                )}
              </View>
            ),
            headerLeft: () => null,
          })}
        />

        <Stack.Screen
          name="ListView"
          component={ListView}
          options={({ navigation }) => ({
            title: STRINGS.appTitle + " - " + STRINGS.listViewTitle,
            headerTitle: STRINGS.appTitle,
            headerRight: () => (
              <View style={styles.headerButtonsContainer}>
                <TouchableOpacity
                  style={styles.iconButton}
                  onPress={() =>
                    navigation.navigate("LegendsView", {
                      screen: STRINGS.listViewTitle,
                    })
                  }
                  aria-label={STRINGS.information}
                >
                  <FontAwesomeIcon icon={faCircleInfo} size={iconSize} color={COLORS.primary} />
                </TouchableOpacity>
                {filterState === "true" && (
                  <TouchableOpacity
                    style={styles.iconButton}
                    onPress={() => navigation.navigate("FilterView")}
                    aria-label={STRINGS.filter}
                  >
                    <FontAwesomeIcon icon={faFilter} size={iconSize} color={COLORS.primary} />
                  </TouchableOpacity>
                )}
              </View>
            ),
            headerTitleStyle: styles.headerTitle,
          })}
        />
        <Stack.Screen
          name="FilterView"
          component={FilterView}
          options={({ navigation }) => ({
            title: STRINGS.appTitle + " - " + STRINGS.filterViewTitle,
            headerTitle: STRINGS.filterViewTitle,
            headerTitleStyle: styles.headerTitle,
            headerRight: () => (
              <TouchableOpacity
                style={styles.iconButton}
                onPress={() =>
                  navigation.navigate("LegendsView", {
                    screen: STRINGS.filterViewTitle,
                  })
                }
                aria-label={STRINGS.information}
              >
                <FontAwesomeIcon icon={faCircleInfo} size={iconSize} color={COLORS.primary} />
              </TouchableOpacity>
            ),
          })}
        />
        <Stack.Screen
          name="DetailsView"
          component={DetailsView}
          options={({ navigation }) => ({
            title: STRINGS.appTitle + " - " + STRINGS.detailsViewTitle,
            headerTitle: "",
            headerTitleStyle: styles.headerTitle,
            headerRight: () => (
              <View style={styles.headerButtonsContainer}>
                <TouchableOpacity
                  style={styles.iconButton}
                  onPress={() =>
                    navigation.navigate("LegendsView", {
                      screen: STRINGS.detailsViewTitle,
                    })
                  }
                  aria-label={STRINGS.information}
                >
                  <FontAwesomeIcon icon={faCircleInfo} size={iconSize} color={COLORS.primary} />
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.iconButton}
                  onPress={() => shareDetails(linking.config.screens.DetailsView)}
                  aria-label={STRINGS.detailsViewTitle}
                >
                  <FontAwesomeIcon style={{ color: COLORS.primary }} icon={faShareNodes} size={iconSize} />
                </TouchableOpacity>
              </View>
            ),
            headerLeft: () => (
              <View style={styles.headerButtonsContainer}>
                <TouchableOpacity
                  onPress={async () => {
                    if (!navigation.canGoBack()) {
                      await AsyncStorage.clear();
                      window.location.href = window.location.origin;
                    }
                  }}
                  aria-label={STRINGS.information}
                >
                  {navigation.canGoBack() ? (
                    <HeaderBackButton tintColor={COLORS.textDark} onPress={() => navigation.goBack()} />
                  ) : (
                    <FontAwesomeIcon style={styles.homeButton} icon={faHome} size={iconSize} color={COLORS.primary} />
                  )}
                </TouchableOpacity>
              </View>
            ),
          })}
        />
        <Stack.Screen
          name="StateDetailsView"
          component={StateDetailsView}
          options={({ navigation }) => ({
            title: STRINGS.appTitle + " - " + STRINGS.detailsViewTitle,
            headerTitle: "",
            headerTitleStyle: styles.headerTitle,
            headerRight: () => (
              <View style={styles.headerButtonsContainer}>
                <TouchableOpacity
                  style={styles.iconButton}
                  onPress={() =>
                    navigation.navigate("LegendsView", {
                      screen: STRINGS.detailsViewTitle,
                    })
                  }
                  aria-label={STRINGS.information}
                >
                  <FontAwesomeIcon icon={faCircleInfo} size={iconSize} color={COLORS.primary} />
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.iconButton}
                  onPress={() => shareDetailsState(linking.config.screens.StateDetailsView)}
                  aria-label={STRINGS.detailsViewTitle}
                >
                  <FontAwesomeIcon style={{ color: COLORS.primary }} icon={faShareNodes} size={iconSize} />
                </TouchableOpacity>
              </View>
            ),
            headerLeft: () => (
              <View style={styles.headerButtonsContainer}>
                <TouchableOpacity
                  onPress={async () => {
                    if (!navigation.canGoBack()) {
                      await AsyncStorage.clear();
                      window.location.href = window.location.origin;
                    }
                  }}
                  aria-label={STRINGS.information}
                >
                  {navigation.canGoBack() ? (
                    <HeaderBackButton tintColor={COLORS.textDark} onPress={() => navigation.goBack()} />
                  ) : (
                    <FontAwesomeIcon style={styles.homeButton} icon={faHome} size={iconSize} color={COLORS.primary} />
                  )}
                </TouchableOpacity>
              </View>
            ),
          })}
        />

        <Stack.Screen
          name="LegendsView"
          component={LegendsView}
          options={{
            title: STRINGS.appTitle + " - " + STRINGS.legendsViewTitle,
            headerTitle: STRINGS.legendsViewTitle,
            headerTitleStyle: styles.headerTitle,
          }}
        />
      </Stack.Navigator>
    </NavigationContainer>
  );
};

/**
 * Styles for the AppCommon component, including headers and icon buttons.
 */
const styles = StyleSheet.create({
  headerTitle: {
    fontWeight: "900",
    color: COLORS.textDark,
    fontSize: 20,
  },
  headerButtonsContainer: {
    flexDirection: "row",
  },
  iconButton: {
    ...Platform.select({
      ios: {
        marginRight: 8,
        padding: 8,
        paddingLeft: 0,
      },
      android: {
        marginRight: 8,
        padding: 8,
        paddingLeft: 0,
      },
      web: {
        marginRight: 16,
        padding: 8,
        paddingLeft: 0,
      },
    }),
  },
  homeButton: {
    ...Platform.select({
      ios: {
        marginRight: 0,
        padding: 8,
        paddingLeft: 8,
      },
      android: {
        marginRight: 0,
        padding: 8,
        paddingLeft: 8,
      },
      web: {
        marginRight: 0,
        padding: 8,
        paddingLeft: 16,
      },
    }),
  },
});

export default AppCommon;
